import { useEffect, useState } from "react";
import { Accordion, Modal } from "react-bootstrap";
import styled from "styled-components";

import { NameID } from "common/types/NameID";
import { MapArea, MapPoint } from "common/types/MapArea";

import DraggableModal from "../molecules/DraggableModal";
import { ModalButtons } from "../molecules/ModalButtons";
import { Map2D } from "./Map2D";
import { SelectPresetAreaList } from "./SelectPresetAreaList";
import { SelectByPointPanel } from "./SelectByPointPanel";
import { Coordinates, PolygonArea } from "./common/types/MapData";
import { isPointInPolygon } from "./common/MapUtil";
import { CURSOR } from "./common/osm/components/CursorDrawer";
import { PinDrawer } from "./common/osm/components/PinDrawer";
import { PolygonDrawer } from "./common/osm/components/PolygonDrawer";

type Props = {
  show: boolean;
  title: string;
  lists: MapArea[];
  initialAreas?: NameID[];
  initialPoints?: MapPoint;
  areaSelected: (areas: NameID[]) => void;
  pointSelected: (points: MapPoint) => void;
  onCancel: () => void;
};

enum SelectBy {
  BY_AREA = "byArea",
  BY_POINT = "byPoint",
}

export const SelectMapAreaDlg = (props: Props) => {
  const { show, title, onCancel } = props;
  const [selectBy, setSelectBy] = useState(SelectBy.BY_AREA);
  const [cursorType, setCursorType] = useState(CURSOR.NONE);

  // 地区で指定用(複数)
  const [selectedAreas, setSelectedAreas] = useState<NameID[]>([]);
  const [polygons, setPolygons] = useState<PolygonArea[]>([]);
  const [selectedPolygonIds, setSelectedPolygonIds] = useState<string[]>([]);

  // 位置で指定用
  const [selectedPoint, setSelectedPoint] = useState<MapPoint>();

  /**
   * 全体の変更をするチェックボックスのチェックが押された時の処理
   * 全ての行のチェックのオンオフを切り替える
   */
  const selectedsChanged = (val: NameID[]) => {
    setSelectedAreas(val);
    setSelectedPolygonIds(
      props.lists.flatMap((x) =>
        x.polygon && val.find((y) => y.id === x.area.id) !== undefined ? [x.polygon.id] : [],
      ),
    );
  };

  const initPolygons = () => {
    setPolygons(
      props.lists.flatMap((val) => (val.polygon ? [{ ...val.polygon, label: val.area.name }] : [])),
    );
  };

  const cleanup = () => {
    setPolygons([]);
    setSelectedPolygonIds([]);
  };

  const startAreaSelection = () => {
    setSelectBy(SelectBy.BY_AREA);
    setCursorType(CURSOR.NONE);
    selectPolygons(selectedAreas, true);
  };

  const startPointSelection = () => {
    setSelectBy(SelectBy.BY_POINT);
    setCursorType(CURSOR.CROSS_HAIR);
    selectPolygons(selectedAreas, false);
  };

  const selectPolygons = (targets: NameID[], select: boolean) => {
    const targetIds = targets.flatMap((x) => {
      const targ = props.lists.find((y) => y.area.id === x.id);
      return targ?.polygon ? [targ.polygon.id] : [];
    });

    setSelectedPolygonIds((prev) => {
      if (select) {
        return Array.from(new Set([...prev, ...targetIds]));
      }
      return prev.filter((id) => !targetIds.includes(id));
    });
  };

  const onMapClick = (point: Coordinates) => {
    if (selectBy !== SelectBy.BY_POINT) return;

    const areaBelongs = props.lists.find(
      (x) => x.polygon?.coords && isPointInPolygon(point, x.polygon.coords),
    );
    setSelectedPoint((prev) => ({
      ...(prev ?? { title: "" }),
      coords: point,
      area: areaBelongs?.area,
    }));
  };

  const onOk = () => {
    if (selectBy === SelectBy.BY_AREA) {
      props.areaSelected(selectedAreas);
    } else {
      if (selectedPoint) props.pointSelected(selectedPoint);
      else props.areaSelected([]);
    }
  };

  useEffect(() => {
    // 全ての地区を追加
    initPolygons();

    setSelectedAreas(props.initialAreas ?? []);
    setSelectedPoint(props.initialPoints);
    if (props.initialAreas) {
      selectPolygons(props.initialAreas, true);
    }

    const isPointSelection = !!props.initialPoints;
    setSelectBy(isPointSelection ? SelectBy.BY_POINT : SelectBy.BY_AREA);
    setCursorType(isPointSelection ? CURSOR.CROSS_HAIR : CURSOR.NONE);

    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SModal show={show} centered dialogAs={DraggableModal}>
      <Modal.Header className="handle">
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <SModalBody>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Map2D
            onMapClick={onMapClick}
            cursorType={cursorType}
            mapFunctions={[
              <PolygonDrawer
                key="polygons"
                polygons={polygons}
                selectedPolygonIds={selectedPolygonIds}
              />,
              <PinDrawer
                key="pins"
                pins={
                  selectBy === SelectBy.BY_POINT && selectedPoint
                    ? [{ point: selectedPoint.coords, label: selectedPoint.title }]
                    : undefined
                }
              />,
            ]}
          />
        </div>
        <SSidePanel>
          <SAccordion activeKey={selectBy}>
            <Accordion.Item
              eventKey={SelectBy.BY_AREA}
              onClick={startAreaSelection}
              className={selectBy === SelectBy.BY_AREA ? "active-item" : undefined}
            >
              <Accordion.Header>地区で指定</Accordion.Header>
              <SAccordionBody>
                <SelectPresetAreaList
                  allArea={props.lists}
                  selecteds={selectedAreas ?? []}
                  selectedsChanged={selectedsChanged}
                />
              </SAccordionBody>
            </Accordion.Item>
            <Accordion.Item
              eventKey={SelectBy.BY_POINT}
              onClick={startPointSelection}
              className={selectBy === SelectBy.BY_POINT ? "active-item" : undefined}
            >
              <Accordion.Header>位置で指定</Accordion.Header>
              <SAccordionBody>
                <SelectByPointPanel
                  title={selectedPoint?.title}
                  onTitleChanged={(title) =>
                    setSelectedPoint((prev) => prev && { ...prev, title: title })
                  }
                  areaName={selectedPoint?.area?.name ?? ""}
                />
              </SAccordionBody>
            </Accordion.Item>
          </SAccordion>
        </SSidePanel>
      </SModalBody>
      <Modal.Footer>
        <ModalButtons onOk={onOk} onCancel={onCancel} />
      </Modal.Footer>
    </SModal>
  );
};

const SModal = styled(Modal)`
  .modal-dialog {
    max-width: 1200px;
  }
`;

const SModalBody = styled(Modal.Body)`
  display: flex;
  flex-direction: row;
  height: 500px;
  overflow: hidden;
  gap: 1rem;
  padding: 0.5rem 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    height: 70vh;
    max-height: 500px;

    > div:first-child {
      flex: 1 1 auto;
      min-height: 0;
    }

    > div:last-child {
      width: 100% !important;
      flex: 0 0 180px;
      overflow: auto;
    }
  }
`;

const SSidePanel = styled.div`
  width: 250px;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const SAccordion = styled(Accordion)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  .accordion-item.active-item {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
  }

  .accordion-item.active-item .accordion-collapse {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
  }
`;

const SAccordionBody = styled(Accordion.Body)`
  padding: 0.5rem;
  flex: 1;
  min-height: 0;
`;
